/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment';

import {
  ActivityLog,
  ActivityLogFilterParams,
  ActivityLogMethods,
  ActivityLogOtherMethods,
  ActivityLogResources
} from '../../../../ts/activity-log';
import { CreateAsyncThunk } from '../../../../ts/state';
import * as API from '../../api/activity-log';
import { renderActivityLogItem } from '../../components/activity-log/util';
import { saveAs } from '../../util/download';
import { capitalizeFirstLetter } from '../../util/string';
import { ranProcessEvents } from '../../util/trackers/segment';
import { addErrorMessage } from '../messages/actions';
import { getAssistantId } from '../session/selectors';

const parseSortInfo = (sortInfo: {
  sortType: string;
  sortDirection: string;
}): string => {
  if (sortInfo?.sortType === 'timestamp') {
    if (sortInfo.sortDirection === 'DESC') {
      return 'modified';
    } else {
      return '-modified';
    }
  } else if (sortInfo?.sortType === 'tooling_item') {
    if (sortInfo.sortDirection === 'DESC') {
      return 'tooling_item';
    } else {
      return '-tooling_item';
    }
  } else {
    return '-modified';
  }
};

const parseFilters = (filterInfo: ActivityLogFilterParams): string => {
  let filterString = 'status:2,';
  if (filterInfo.detail && filterInfo.detail !== '') {
    filterString =
      filterString + `tooling_description:${filterInfo.detail.trim()},`;
  }
  if (filterInfo.activity && filterInfo.activity.length > 0) {
    const filterActivity = [...filterInfo.activity];
    if (filterActivity.includes(ActivityLogMethods.DELETED)) {
      filterActivity.push(ActivityLogOtherMethods.BULK_DELETED);
    }
    filterString =
      filterString +
      `tooling_method::${filterActivity.map((a: string) => `${a},`)}`;
  }
  if (filterInfo.item && filterInfo.item.length > 0) {
    filterString =
      filterString +
      `tooling_item::${filterInfo.item.map((i: string) => `${i},`)}`;
  } else {
    filterString = filterString + 'tooling_item::!skill,';
  }
  if (filterInfo.environment && filterInfo.environment.length > 0) {
    filterString =
      filterString +
      `environment_name::${filterInfo.environment.map((e: string) => `${e},`)}`;
  }
  if (filterInfo.environment_ids && filterInfo.environment_ids.length > 0) {
    filterString =
      filterString +
      `environment_id::${filterInfo.environment_ids.map(
        (e: string) => `${e},`
      )}`;
  }
  if (filterInfo.user && filterInfo.user !== '') {
    filterString = filterString + `email:${filterInfo.user.trim()}`;
  }

  return filterString;
};

const convertResultsToCsv = (results: ActivityLog[]): string[] => {
  const firstRow = 'Items,Details,Activity,Environment,User,Date/Time\n';
  const localTimezone = moment.tz.guess(); // use user's timezone

  const parsedResults = results
    .filter(
      log =>
        !(
          log._tooling_item === ActivityLogResources.EXTENSION &&
          log._tooling_method === ActivityLogMethods.CREATED
        ) // PM decision to hide segment.create events
    )
    .map(
      result =>
        `"${renderActivityLogItem(result)}","${capitalizeFirstLetter(
          result?._tooling_description || ''
        )}","${capitalizeFirstLetter(
          result?._tooling_method || ''
        )}","${capitalizeFirstLetter(
          result.event.target.assistant.environment.name || '-'
        )}","${result.actor.email}",${moment
          .tz(`${result.timestamp}Z`, localTimezone)
          .format('MM-DD-YYYY[ ]HH:mm:ss A')}\n`
    );
  return [...firstRow, ...parsedResults];
};

export const listActivityLogs = createAsyncThunk<any, any, CreateAsyncThunk>(
  'activityLog/list',
  async (params, { dispatch, getState }) => {
    try {
      const assistantId = getAssistantId(getState());
      const queryParams = {
        startDate:
          params.startDate?.format('YYYY-MM-DD[T]HH:mm:ss[Z]') ||
          params.endDate
            ?.subtract(3, 'months')
            .format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
        endDate: params.endDate?.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
        sort: parseSortInfo(params.sort),
        filter: params.filters && parseFilters(params.filters)
      };

      if (!assistantId) {
        return [];
      }

      const paginatedResults: API.PaginatedActivityLogs =
        await API.listActivityLogs(assistantId, queryParams);

      ranProcessEvents.activityLogTableUpdated({
        category: params.eventCategory
      });
      return paginatedResults;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

// Adding this action so that we can handle pagination separately
export const listActivityLogsNextPage = createAsyncThunk<
  any,
  any,
  CreateAsyncThunk
>('activityLog/listNextPage', async ({ nextUrl }, { dispatch }) => {
  try {
    const results: API.PaginatedActivityLogs = await API.listActivityLogsByUrl(
      nextUrl
    );

    return results;
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const downloadActivityLogs = createAsyncThunk<
  any,
  any,
  CreateAsyncThunk
>('activityLog/download', async (params, { dispatch, getState }) => {
  try {
    const assistantId = getAssistantId(getState());
    const queryParams = {
      startDate:
        params.startDate?.format('YYYY-MM-DD[T]HH:mm:ss[Z]') ||
        params.endDate
          ?.subtract(3, 'months')
          .format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
      endDate: params.endDate?.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
      sort: parseSortInfo(params.sort),
      filter: params.filters && parseFilters(params.filters)
    };

    if (!assistantId) {
      return [];
    }

    let paginatedResults: API.PaginatedActivityLogs;
    paginatedResults = await API.listActivityLogs(assistantId, queryParams);
    const results = [...paginatedResults.event_logs];

    while (paginatedResults?.pagination?.next_url) {
      paginatedResults = await API.listActivityLogsByUrl(
        paginatedResults.pagination.next_url
      );
      results.push(...paginatedResults.event_logs);
    }

    const file = new Blob(convertResultsToCsv(results), {
      type: 'text/csv; charset=UTF-8'
    });

    saveAs(URL.createObjectURL(file), 'activity-log.csv');
    return results;
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});
